import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Home, Search, Send, History, Menu, Star, Book, Info, Phone, Bell, Settings } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { fetchConsumerNotifications } from '@/services/consumerNotifications';
import { supabase } from '@/integrations/supabase/client';
import { useWaitingQuoteCount } from '@/hooks/useWaitingQuoteCount';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/providers/DirectionProvider';
import RequestDialog from '@/components/dashboard/RequestDialog';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';

const BottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { direction } = useDirection();
  const [notificationCount, setNotificationCount] = React.useState<number>(0);
  const [waitingForRatingCount, setWaitingForRatingCount] = React.useState<number>(0);
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const waitingQuotes = useWaitingQuoteCount();

  React.useEffect(() => {
    const getNotifications = async () => {
      if (user) {
        try {
          const notifications = await fetchConsumerNotifications();
          const unreadCount = notifications.filter(n => !n.isRead).length;
          setNotificationCount(unreadCount);
        } catch (error) {
          console.error("Failed to fetch notifications:", error);
        }
      }
    };

    const getWaitingForRatingCount = async () => {
      if (user) {
        try {
          const { data, error } = await supabase
            .from('requests')
            .select('id')
            .eq('user_id', user.id)
            .eq('status', 'waiting_for_rating');
            
          if (!error && data) {
            setWaitingForRatingCount(data.length);
          }
        } catch (error) {
          console.error("Failed to fetch waiting for rating count:", error);
        }
      }
    };

    if (user) {
      getNotifications();
      getWaitingForRatingCount();
    }
  }, [user]);

  const handleSendRequest = () => {
    setIsRequestDialogOpen(true);
  };

  const navItems = [
    {
      icon: Home,
      label: user
        ? (waitingForRatingCount > 0 ? t('bottomNav.ratingRequired') : t('bottomNav.dashboard'))
        : t('bottomNav.home'),
      action: () => {
        const targetPath = user ? '/dashboard' : '/';
        if (location.pathname !== targetPath) {
          navigate(targetPath);
        }
      },
      isActive: user 
        ? location.pathname === '/dashboard'
        : location.pathname === '/',
      hasWaitingRating: user ? waitingForRatingCount > 0 : false,
      waitingCount: waitingForRatingCount,
      // A rating to give wins the badge; otherwise quotes waiting for a decision
      waitingQuotes: user && waitingForRatingCount === 0 ? waitingQuotes : 0
    },
    {
      icon: Search,
      label: t('bottomNav.search'),
      action: () => navigate('/search'),
      isActive: location.pathname === '/search'
    },
    {
      icon: Send,
      label: t('bottomNav.request'),
      action: handleSendRequest,
      isActive: false,
      isSpecial: true
    },
    {
      icon: History,
      label: t('bottomNav.workHistory'),
      action: () => navigate('/work-history'),
      isActive: location.pathname === '/work-history' || location.pathname.startsWith('/work-history/')
    },
    {
      icon: Menu,
      label: t('bottomNav.more'),
      action: () => setIsMenuOpen(true),
      isActive: false
    }
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 md:hidden pb-[env(safe-area-inset-bottom)]">
      <div className="flex justify-around items-center py-1">
        {navItems.map((item, index) => (
          <button
            key={index}
            onClick={item.action}
            className={`flex flex-col items-center justify-center px-3 py-2 relative ${
              item.isSpecial 
                ? 'text-teal-700' 
                : item.isActive 
                  ? 'text-blue-600' 
                  : 'text-gray-600'
            } transition-colors duration-200`}
          >
            <div className="relative">
              <item.icon 
                size={20} 
                className={item.isSpecial ? 'text-teal-700' : ''} 
              />
              {item.hasWaitingRating && (
                <>
                  <Star size={12} className="absolute -bottom-1 -left-1 text-amber-500 fill-amber-500" />
                  <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs w-4 h-4 flex items-center justify-center rounded-full">
                    {item.waitingCount}
                  </span>
                </>
              )}
              {item.waitingQuotes > 0 && (
                <span
                  className="absolute -top-1 -right-2 bg-blue-600 text-white text-xs min-w-4 h-4 px-1 flex items-center justify-center rounded-full"
                  aria-label={t('requestCard.waitingQuotes', { count: item.waitingQuotes })}
                >
                  {item.waitingQuotes}
                </span>
              )}
            </div>
            <span className="text-xs mt-1">{item.label}</span>
          </button>
        ))}
      </div>
      <RequestDialog 
        isOpen={isRequestDialogOpen} 
        onOpenChange={setIsRequestDialogOpen}
      />
      
      {/* Menu Sheet */}
      <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <SheetContent side="bottom" className="h-auto max-h-[60vh] rounded-t-2xl" dir={direction}>
          <div className="flex flex-col gap-2 py-4">
            <Link 
              to="/articles" 
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
            >
              <Book size={20} className="text-primary" />
              <span>{t('mobileMenu.tipsAndArticles')}</span>
            </Link>
            <Link 
              to="/about" 
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
            >
              <Info size={20} className="text-primary" />
              <span>{t('mobileMenu.about')}</span>
            </Link>
            <Link 
              to="/contact" 
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
            >
              <Phone size={20} className="text-primary" />
              <span>{t('mobileMenu.contact')}</span>
            </Link>
            
            <Separator className="my-2" />
            
            <Link 
              to="/dashboard?tab=notifications" 
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
            >
              <Bell size={20} className="text-primary" />
              <span>{t('mobileMenu.notifications')}</span>
              {notificationCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full mr-auto">
                  {notificationCount}
                </span>
              )}
            </Link>
            <Link 
              to="/settings" 
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
            >
              <Settings size={20} className="text-primary" />
              <span>{t('mobileMenu.settings')}</span>
            </Link>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default BottomNavigation;